<template>
  <div class="panorama-compass-wrapper" v-show="visible">
    <v-tooltip bottom open-delay="400" :disabled="!visible">
      <template v-slot:activator="{ on, attrs }">
        <div
          v-bind="attrs"
          class="panorama-compass"
          :class="{
            'is-snapping': isSnapping,
            'is-disabled': !isReady,
            'is-idle': isIdle,
            'reduced-motion': prefersReducedMotion,
          }"
          role="button"
          :tabindex="isReady ? 0 : -1"
          :aria-label="ariaLabel"
          :aria-disabled="isReady ? 'false' : 'true'"
          @click="handleActivate"
          @dblclick="handleDoubleActivate"
          @keydown.enter.prevent="handleActivate"
          @keydown.space.prevent="handleActivate"
          @mouseenter="onIdleReset"
          v-on="on"
        >
          <div class="compass-backdrop" aria-hidden="true"></div>

          <svg
            ref="rose"
            class="compass-rose"
            :class="{ snapping: isSnapping }"
            :style="roseRotationStyle"
            viewBox="0 0 64 64"
            aria-hidden="true"
          >
            <circle class="rose-ring" cx="32" cy="32" r="28" />

            <g class="rim-ticks">
              <line
                v-for="(t, i) in rimTicks"
                :key="i"
                :x1="t.x1"
                :y1="t.y1"
                :x2="t.x2"
                :y2="t.y2"
                :class="{ 'tick-cardinal': t.isCardinal }"
              />
            </g>

            <polygon
              class="rose-n-tick"
              :class="{ pulse: nTickPulse }"
              points="32,3 28,11 36,11"
            />
            <text class="rose-label rose-label-n" x="32" y="22" text-anchor="middle">N</text>
            <text class="rose-label" x="51" y="39" text-anchor="middle">E</text>
            <text class="rose-label" x="32" y="58" text-anchor="middle">S</text>
            <text class="rose-label" x="13" y="39" text-anchor="middle">W</text>
          </svg>

          <!-- Fixed center indicator: two-tone needle + inverted pivot pin.
               V3 Minimal Dark uses flat fills via CSS (no SVG gradient defs)
               so color direction lives in one place (the scoped stylesheet
               below) and is trivial to re-theme later. The emerald north half
               + white south half preserves the classic compass convention;
               the pivot pin is inverted (white outer ring + dark inner dot)
               to read cleanly on the flat dark backdrop. -->
          <svg class="compass-indicator" viewBox="0 0 64 64" aria-hidden="true">
            <!-- Round 6.4: needle waist widened 7→8 viewBox units for ~10px
                 presence at 80px render, and pivot pin scaled 13% for
                 proportional visual authority at the larger size. -->
            <polygon class="needle-half needle-north" points="32,13 36,32 28,32" />
            <polygon class="needle-half needle-south" points="32,51 36,32 28,32" />
            <line class="needle-highlight" x1="32" y1="13" x2="32" y2="51" />
            <circle class="pivot-outer" cx="32" cy="32" r="3.6" />
            <circle class="pivot-inner" cx="32" cy="32" r="2.3" />
          </svg>
        </div>
      </template>
      <span>{{ tooltipText }}</span>
    </v-tooltip>

    <!-- Screen-reader status region. CRITICAL: sibling of the role=button
         container, NOT a descendant. ARIA 1.2 forbids nesting live regions
         inside interactive elements. -->
    <span ref="srAnnounce" class="ds-sr-only" role="status" aria-atomic="true"></span>
  </div>
</template>

<script>
  import {
    normalizeHeading,
    roseRotation,
    formatHeading,
    SNAP_DURATION_MS,
    SNAP_COAST_SLACK_MS,
    IDLE_FADE_DELAY_MS,
    SINGLE_CLICK_DELAY_MS,
    N_TICK_PULSE_MS,
    levelPitch,
  } from '../utils/compass.js';
  import { log as debugLog } from '@/utils/debugLogger';

  export default {
    name: 'PanoramaCompass',
    props: {
      // Imperative yaw poll — called from rAF loop. Must be a function because
      // Pannellum emits no yaw-change events and we poll during interaction.
      getYaw: {
        type: Function,
        required: true,
      },
      // Reactive value prop — drives isReady and the rotation math. Round 5
      // lifted this value into top-level Vuex state so cross-component
      // reactivity works end-to-end.
      northOffset: {
        type: Number,
        default: null,
      },
      // Imperative snap action — called on click. yawOnly=true preserves pitch,
      // yawOnly=false also levels pitch toward 0 when steep.
      lookAtNorth: {
        type: Function,
        required: true,
      },
      // Drives rAF gating — when true, the component polls getYaw() on every
      // animation frame. Also drives idle-fade reset.
      isInteracting: {
        type: Boolean,
        default: false,
      },
      // Triggers a clean reset when the user switches to a different scan.
      // Also determines whether the compass is "visible" at all (no scan → hide).
      selectedImageId: {
        type: [String, Number],
        default: null,
      },
    },
    data() {
      return {
        currentYaw: 0,
        rafId: null,
        isSnapping: false,
        snapTimeoutId: null,
        singleClickTimerId: null,
        pulseTimerId: null,
        idleTimerId: null,
        isIdle: false,
        nTickPulse: false,
        prefersReducedMotion: false,
        // Single matchMedia instance — round 6 fix for the double-call listener leak.
        // Lazy-initialized in mounted() and reused in beforeDestroy() so
        // removeEventListener targets the same MediaQueryList that addEventListener did.
        reducedMotionMql: null,
      };
    },
    computed: {
      visible() {
        // Render whenever we have a scan context, even if the scan is missing
        // a valid northOffset — in that case the compass enters a disabled
        // ghost state instead of disappearing silently.
        return this.selectedImageId != null;
      },
      isReady() {
        return this.northOffset != null && Number.isFinite(this.northOffset);
      },
      headingDegrees() {
        if (!this.isReady) return 0;
        return normalizeHeading(this.currentYaw, this.northOffset);
      },
      headingDisplay() {
        if (!this.isReady) return '';
        return formatHeading(this.headingDegrees);
      },
      roseRotationStyle() {
        if (!this.isReady) {
          return { transform: 'rotate(0deg)' };
        }
        const deg = roseRotation(this.currentYaw, this.northOffset);
        return { transform: `rotate(${deg}deg)` };
      },
      // Round 6 a11y fix: aria-label is now STATIC (except for the ready/loading
      // switch). Per-frame heading updates go into the role="status" sibling span,
      // which screen readers announce at most once per pan-end instead of every rAF.
      ariaLabel() {
        if (!this.visible) return '';
        if (!this.isReady) return 'Compass. Orientation not available for this scan.';
        return 'Compass. Press Enter to face north. Double-click to level view.';
      },
      tooltipText() {
        if (!this.isReady) return 'Orientation not available for this scan';
        return 'Click to face north · Double-click to level view';
      },
      // 12 radial ticks at 30° intervals. Cardinal positions (0°, 90°, 180°, 270°)
      // are marked distinctly so N/E/S/W read as precision anchors, not decoration.
      rimTicks() {
        const ticks = [];
        const cx = 32;
        const cy = 32;
        const rOuter = 29;
        const rInnerMajor = 25;
        const rInnerMinor = 26.5;
        for (let i = 0; i < 12; i++) {
          const isCardinal = i % 3 === 0;
          const angleDeg = i * 30 - 90;
          const angleRad = (angleDeg * Math.PI) / 180;
          const cos = Math.cos(angleRad);
          const sin = Math.sin(angleRad);
          const rInner = isCardinal ? rInnerMajor : rInnerMinor;
          ticks.push({
            x1: +(cx + rOuter * cos).toFixed(3),
            y1: +(cy + rOuter * sin).toFixed(3),
            x2: +(cx + rInner * cos).toFixed(3),
            y2: +(cy + rInner * sin).toFixed(3),
            isCardinal,
          });
        }
        return ticks;
      },
    },
    watch: {
      isInteracting(next) {
        if (next) {
          this.cancelIdleFade();
          this.startRaf();
        } else if (!this.isSnapping) {
          this.stopRaf();
          this.announceHeading();
          this.startIdleTimer();
        }
      },
      selectedImageId() {
        this.stopRaf();
        this.clearSnap();
        this.clearSingleClickTimer();
        this.clearPulseTimer();
        this.cancelIdleFade();
        this.currentYaw = 0;
        // Round 6 fix: reset isSnapping so stale .snapping CSS class doesn't stick.
        this.isSnapping = false;
        this.nTickPulse = false;
        this.isIdle = false;
        this.$nextTick(() => {
          if (this.isInteracting) this.startRaf();
          this.startIdleTimer();
        });
      },
    },
    mounted() {
      // Round 6 fix: store a single MediaQueryList reference so beforeDestroy
      // can remove the listener from the SAME instance that received addEventListener.
      if (window.matchMedia) {
        this.reducedMotionMql = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.prefersReducedMotion = this.reducedMotionMql.matches;
        if (this.reducedMotionMql.addEventListener) {
          this.reducedMotionMql.addEventListener('change', this.onReducedMotionChange);
        }
      }
      this.startIdleTimer();
      debugLog('[PanoramaCompass] mounted', { selectedImageId: this.selectedImageId });
    },
    beforeDestroy() {
      this.stopRaf();
      this.clearSnap();
      this.clearSingleClickTimer();
      this.clearPulseTimer();
      this.cancelIdleFade();
      if (this.reducedMotionMql && this.reducedMotionMql.removeEventListener) {
        this.reducedMotionMql.removeEventListener('change', this.onReducedMotionChange);
      }
      this.reducedMotionMql = null;
    },
    methods: {
      onReducedMotionChange(event) {
        this.prefersReducedMotion = event.matches;
      },
      startRaf() {
        if (this.rafId != null) return;
        this.tick();
      },
      stopRaf() {
        if (this.rafId != null) {
          cancelAnimationFrame(this.rafId);
          this.rafId = null;
        }
      },
      tick() {
        const yaw = this.getYaw();
        if (yaw != null && yaw !== this.currentYaw) {
          this.currentYaw = yaw;
        }
        if (this.isInteracting || this.isSnapping) {
          this.rafId = requestAnimationFrame(this.tick);
        } else {
          this.rafId = null;
        }
      },
      // Round 6 UX: single click defers its action briefly so a double-click
      // can cancel it. Single = yaw only (preserves pitch). Double = yaw + level.
      handleActivate() {
        if (!this.isReady) return;
        this.onIdleReset();
        this.clearSingleClickTimer();
        this.singleClickTimerId = setTimeout(() => {
          this.singleClickTimerId = null;
          debugLog('[PanoramaCompass] snap-to-north requested (yaw only)');
          this.performSnap(false);
        }, SINGLE_CLICK_DELAY_MS);
      },
      handleDoubleActivate() {
        if (!this.isReady) return;
        this.onIdleReset();
        this.clearSingleClickTimer();
        debugLog('[PanoramaCompass] snap-to-north requested (yaw + level pitch)');
        this.performSnap(true);
      },
      performSnap(levelPitchToo) {
        // Round 6 a11y fix: announce snap START immediately so keyboard users
        // get feedback before the 600ms animation completes.
        this.setSrAnnouncement(levelPitchToo ? 'Snapping to north and leveling view' : 'Snapping to north');
        this.startSnap();
        const duration = this.prefersReducedMotion ? 0 : SNAP_DURATION_MS;
        this.lookAtNorth(duration, levelPitchToo);
      },
      startSnap() {
        this.isSnapping = true;
        this.startRaf();
        this.clearSnap();
        const duration = this.prefersReducedMotion ? 0 : SNAP_DURATION_MS;
        this.snapTimeoutId = setTimeout(() => {
          this.finishSnap();
        }, duration + SNAP_COAST_SLACK_MS);
      },
      finishSnap() {
        this.isSnapping = false;
        if (!this.isInteracting) {
          this.stopRaf();
        }
        this.triggerNTickPulse();
        this.announceHeading();
      },
      clearSnap() {
        if (this.snapTimeoutId != null) {
          clearTimeout(this.snapTimeoutId);
          this.snapTimeoutId = null;
        }
      },
      clearSingleClickTimer() {
        if (this.singleClickTimerId != null) {
          clearTimeout(this.singleClickTimerId);
          this.singleClickTimerId = null;
        }
      },
      clearPulseTimer() {
        if (this.pulseTimerId != null) {
          clearTimeout(this.pulseTimerId);
          this.pulseTimerId = null;
        }
      },
      triggerNTickPulse() {
        if (this.prefersReducedMotion) return;
        this.nTickPulse = true;
        this.clearPulseTimer();
        this.pulseTimerId = setTimeout(() => {
          this.nTickPulse = false;
          this.pulseTimerId = null;
        }, N_TICK_PULSE_MS);
      },
      // Round 6 a11y fix: clear-then-set pattern so SRs re-announce identical
      // headings (e.g., panning back to 045° from 090°).
      setSrAnnouncement(text) {
        const span = this.$refs.srAnnounce;
        if (!span) return;
        span.textContent = '';
        this.$nextTick(() => {
          if (this.$refs.srAnnounce) {
            this.$refs.srAnnounce.textContent = text;
          }
        });
      },
      announceHeading() {
        if (!this.isReady) return;
        this.setSrAnnouncement(this.headingDisplay);
      },
      startIdleTimer() {
        this.cancelIdleFade();
        if (this.prefersReducedMotion) return;
        this.idleTimerId = setTimeout(() => {
          this.isIdle = true;
          this.idleTimerId = null;
        }, IDLE_FADE_DELAY_MS);
      },
      cancelIdleFade() {
        if (this.idleTimerId != null) {
          clearTimeout(this.idleTimerId);
          this.idleTimerId = null;
        }
      },
      onIdleReset() {
        this.isIdle = false;
        this.startIdleTimer();
      },
    },
  };
</script>

<style scoped lang="scss">
  .panorama-compass-wrapper {
    position: absolute;
    // Round 6.2: bumped from --ds-space-md (16px) to --ds-space-xl (32px) so the
    // compass sits further off the viewer edge and doesn't crowd any toolbar
    // icons rendered in the same top-right corner area.
    top: var(--ds-space-xl, 32px);
    right: var(--ds-space-xl, 32px);
    z-index: 10;
    pointer-events: none; // The wrapper is passthrough; only .panorama-compass captures clicks.
  }

  .panorama-compass {
    position: relative;
    // Round 6.1: +25% radius bump (64 → 80). SVG viewBox stays 0 0 64 64 so
    // all internal coordinates scale proportionally — no recomputation needed.
    width: 80px;
    height: 80px;
    pointer-events: auto;
    cursor: pointer;
    user-select: none;
    color: #ffffff;
    font-family: var(--ds-font-family-body);
    outline: none;
    opacity: 1;
    transform: scale(1);
    // Round 6: full state transition for hover/press/idle. NOT on transform alone —
    // that would conflict with the rose's separate snap transition on a child SVG.
    transition:
      opacity 300ms ease-out,
      transform 200ms cubic-bezier(0.34, 1.56, 0.64, 1);

    // Round 6 a11y fix: focus ring on the CONTAINER (not the backdrop) using
    // outline + box-shadow double-ring, so it stays visible on any panorama
    // background AND survives forced-colors mode which strips box-shadows.
    &:focus-visible {
      outline: var(--ds-focus-ring-width, 2px) solid var(--ds-focus-ring-color, #2cc295);
      outline-offset: var(--ds-focus-ring-offset, 2px);
      box-shadow: 0 0 0 4px rgba(0, 0, 0, 0.8);
    }

    &:hover:not(.is-disabled) {
      transform: scale(1.04);

      .compass-backdrop {
        // V3 hover: darker flat black + brighter hairline ring.
        // Round 6.4: hover ring bumped 55% → 65% so the hover delta stays
        // proportional to the new 45% default hairline.
        background: rgba(0, 0, 0, 0.6);
        box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.65);
      }
    }

    &:active:not(.is-disabled) {
      transform: scale(0.96);
      transition-duration: 80ms;
    }

    &.is-idle:not(:hover):not(:focus-visible):not(.is-snapping) {
      opacity: 0.55;
    }

    &.is-disabled {
      cursor: default;
      opacity: 0.42;
    }
  }

  .compass-backdrop {
    position: absolute;
    inset: 0;
    border-radius: var(--ds-radius-full, 9999px);
    // Round 6.3: "Minimal Dark" (V3 from the showcase). Flat semi-transparent
    // black disc with a single hairline white ring. No emerald gradient, no
    // 28px drop shadow, no layered inset rim light. Drops the visual weight
    // of the previous recipe while keeping the compass tangible via the white
    // ring edge definition. The mild backdrop-filter blur keeps labels readable
    // over any panorama content (sky / concrete / dark).
    background: rgba(0, 0, 0, 0.48);
    // Round 6.4: hairline ring bumped 35% → 45% alpha. Holds against bright
    // panoramas (snow / sky) without turning into a heavy border.
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.45);
    backdrop-filter: blur(4px) saturate(120%);
    -webkit-backdrop-filter: blur(4px) saturate(120%);
    transition: background 200ms ease-out, box-shadow 200ms ease-out;
  }

  .compass-rose {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
  }

  .compass-rose.snapping {
    transition: transform var(--compass-snap-duration, 600ms) cubic-bezier(0.4, 0, 0.2, 1);
  }

  .reduced-motion .compass-rose.snapping {
    transition: none;
  }

  .rose-ring {
    fill: none;
    // V3: thinner ring stroke (0.55 vs 0.7) since the hairline backdrop border
    // now carries the edge definition that the ring used to provide.
    stroke: rgba(255, 255, 255, 0.55);
    stroke-width: 1;
  }

  .rim-ticks line {
    stroke: rgba(255, 255, 255, 0.5);
    stroke-width: 1;
    stroke-linecap: round;

    &.tick-cardinal {
      // V3: cardinal ticks read as pure white (not 0.9 opacity) for max
      // precision anchor against the flat dark backdrop.
      stroke: #ffffff;
      stroke-width: 1.5;
    }
  }

  .rose-n-tick {
    // V3 color inversion: the N *tick* (triangle at 12 o'clock) is now white,
    // so emerald is reserved exclusively for the N *label* and the north half
    // of the needle. This reduces color density while keeping the brand accent
    // exactly where it carries semantic meaning ("this direction is north").
    fill: #ffffff;
    transform-origin: 32px 8px;

    &.pulse {
      // Snap-complete pulse still uses emerald glow — reads as a brief halo
      // around the white tick, which is visually distinct from the resting state.
      animation: n-tick-pulse 200ms ease-out;
    }
  }

  @keyframes n-tick-pulse {
    0% { filter: drop-shadow(0 0 0 rgba(44, 194, 149, 0)); transform: scale(1); }
    50% { filter: drop-shadow(0 0 6px rgba(44, 194, 149, 0.9)); transform: scale(1.25); }
    100% { filter: drop-shadow(0 0 0 rgba(44, 194, 149, 0)); transform: scale(1); }
  }

  .rose-label {
    fill: #ffffff;
    font-size: var(--ds-font-micro, 10px);
    font-weight: 600;
    font-family: var(--ds-font-family-body);
    // V3: lighter stroke (0.55 alpha, 2px width) and no drop-shadow filter.
    // The flat dark backdrop already provides >4.5:1 contrast against white
    // labels, so the heavy halo the old glass style required is unnecessary.
    paint-order: stroke fill;
    stroke: rgba(0, 0, 0, 0.55);
    stroke-width: 2;
  }

  .rose-label-n {
    fill: var(--ds-brand-emerald, #2cc295);
    font-size: var(--ds-font-caption, 12px);
    font-weight: 700;
  }

  .compass-indicator {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    // V3 drops the drop-shadow entirely — the needle reads as a flat graphic
    // on a flat backdrop. Consistent with the "no depth" minimal theme.
  }

  .needle-half {
    // V3: weaker stroke than the gradient variant (0.4 alpha / 0.3 width) so
    // the flat fills stay readable without a heavy outline around them.
    stroke: rgba(0, 0, 0, 0.4);
    stroke-width: 0.3;
    stroke-linejoin: round;
  }

  .needle-north {
    // Round 6.4: Deliberately brighter than the brand emerald token so the
    // needle's small ~19px-tall north half reads as confident at 80×80 CSS.
    // The N text label (.rose-label-n below) stays on var(--ds-brand-emerald)
    // so brand color remains semantic; this hardcoded variant is scoped to
    // the needle fill only. If --ds-brand-emerald itself is ever refreshed,
    // grep for #34e0a8 and update this single site by hand.
    fill: #34e0a8;
  }

  .needle-south {
    fill: #ffffff;
  }

  .needle-highlight {
    stroke: rgba(255, 255, 255, 0.5);
    stroke-width: 0.35;
    stroke-linecap: round;
  }

  .pivot-outer {
    // V3 inversion: the pivot ring is now white (was black). On a flat dark
    // backdrop, a white ring reads as "this is the rotation center" more clearly
    // than a dark ring would (which would blend into the backdrop).
    fill: rgba(255, 255, 255, 0.9);
  }

  .pivot-inner {
    // Dark inner dot (was a radial gradient) — completes the inverted look:
    // white outer ring + dark center = a bullseye pattern that reads as
    // "pivot point" universally.
    fill: #0b1012;
  }

  // Round 6 a11y fix: forced-colors (Windows High Contrast) fallback.
  // Box-shadows and gradients are stripped in this mode, so we ensure the
  // compass remains outlined and legible using system colors.
  @media (forced-colors: active) {
    .panorama-compass {
      &:focus-visible {
        outline: 3px solid Highlight;
        box-shadow: none;
      }
    }
    .compass-backdrop {
      background: Canvas;
      border: 1px solid CanvasText;
      backdrop-filter: none;
    }
    .rose-ring,
    .rim-ticks line {
      stroke: CanvasText;
    }
    .rose-n-tick,
    .needle-north {
      fill: Highlight;
    }
    .needle-south {
      fill: CanvasText;
    }
    .needle-half {
      stroke: CanvasText;
    }
    .needle-highlight {
      stroke: Canvas;
    }
    .rose-label {
      fill: CanvasText;
      stroke: Canvas;
    }
    .pivot-outer {
      fill: CanvasText;
    }
    .pivot-inner {
      fill: Canvas;
      stroke: CanvasText;
    }
  }
</style>
